import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { DotBadge } from '../../../common/badge/DotBadge';
import { Icon } from '../../../common/icon/Icon';

interface BottomNavigatorButtonProps {
  link: string;
  defaultIcon: string;
  pressedIcon: string;
  isSelected: boolean;
  isExternalLink?: boolean;
  title: string;
  onClick?: () => void;
  hasNotification: boolean;
}

export const BottomNavigatorButton = ({
  link = '#',
  defaultIcon,
  pressedIcon,
  isSelected,
  isExternalLink = false,
  title,
  onClick,
  hasNotification,
}: BottomNavigatorButtonProps) => {
  const navigate = useNavigate();

  const handleExternalLink = (url: string) => {
    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: url });
    } else {
      window.location.href = url;
    }
  };

  return (
    <ButtonWrap itemAliginBottom={isExternalLink}>
      <Button
        onClick={() => {
          ampli.네비게이터버튼클릭({ '버튼 이름': title });

          if (isExternalLink) {
            handleExternalLink(link);
            return;
          }

          if (onClick) {
            onClick();
          }

          if (isSelected) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            navigate(link);
          }
        }}
      >
        {isExternalLink ? (
          <CircleIconWrapper>
            <Icon name={'PluvaShop'} width={32} height={32} />
          </CircleIconWrapper>
        ) : (
          <ButtonImageWrapper>
            <ButtonImage src={pressedIcon} alt={`${title}on`} isShow={isSelected} />
            <ButtonImage src={defaultIcon} alt={title} isShow={!isSelected} />
            {hasNotification && <DotBadge />}
          </ButtonImageWrapper>
        )}

        <ButtonTitle isSelected={isSelected || isExternalLink}>{title}</ButtonTitle>
      </Button>
    </ButtonWrap>
  );
};

const ButtonWrap = styled.li<{ itemAliginBottom?: boolean }>`
  display: flex;
  height: 80px;
  flex-direction: column;
  justify-content: ${(props) => (props.itemAliginBottom ? 'flex-end' : 'center')};
  align-items: center;

  gap: 4px;

  flex: 1 0 0;
`;

const Button = styled.button`
  display: flex;
  padding-bottom: 24px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: var(--radius-small, 4px);
  flex: 1 0 0;
  align-self: stretch;

  border-top: 1px solid var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ButtonImageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const CircleIconWrapper = styled.div`
  position: absolute;
  top: -16px;
  display: flex;
  width: 52px;
  height: 52px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
  box-shadow: 2px 4px 16px 0px rgba(35, 36, 41, 0.25);
`;

const ButtonImage = styled.img<{ isShow: boolean }>`
  width: 28px;
  height: 28px;

  display: ${(props) => (props.isShow ? 'inline-block' : 'none')};

  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-select: none;
`;

const ButtonTitle = styled.p<{ isSelected: boolean }>`
  color: ${(props) => (props.isSelected ? '#232429' : '#babdca')};
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;
